import React, { useCallback, useMemo } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import { getShiftAllowanceMisBaseURL } from '../../../Api/request/shiftAllowanceMis';
import './ShiftAllowanceMis.scss';

const PAGE_SIZE = 10;
const PAGINATION_CONFIG = { page: 1, limit: PAGE_SIZE, rowsPerPageOptions: [] };
const NUMERIC_COLUMN_FIELDS = new Set([
  'morningShiftDays',
  'afternoonShiftDays',
  'nightShiftDays',
  'extraHours',
  'totalHours',
]);

const dedupeTableConfiguration = (columns = []) => {
  if (!Array.isArray(columns)) return columns;

  const seenFields = new Set();
  return columns
    .map((column) => {
      const field = column?.field || '';
      const isFilterOnlyColumn = column?.isGlobalFilter === true || field.startsWith('filter');

      if (isFilterOnlyColumn) {
        return { ...column, visible: false, defaultPinned: false, isPinned: false };
      }

      if (!NUMERIC_COLUMN_FIELDS.has(field)) {
        return { ...column, visible: column?.visible !== false };
      }

      return {
        ...column,
        align: 'right',
        numeric: true,
        headerStyle: { ...(column?.headerStyle || {}), textAlign: 'right' },
        style: { ...(column?.style || {}), textAlign: 'right' },
        visible: column?.visible !== false,
      };
    })
    .filter((column) => {
      const field = column?.field;
      if (!field) return true;
      if (seenFields.has(field)) return false;
      seenFields.add(field);
      return true;
    });
};

const ShiftAllowanceMis = () => {
  const { userProfile } = useAuth();
  const baseURL = getShiftAllowanceMisBaseURL();

  const userInfo = useMemo(() => ({
    employeeId: userProfile?.employeeId || userProfile?.emp_id || '',
    email: userProfile?.email || '',
    name: userProfile?.name || userProfile?.employeeName || '',
  }), [userProfile]);

  const handleApiResponse = useCallback((response) => {
    normalizeTableUtilityResponse(response);
    const tableConfiguration = response?.data?.tableConfiguration || response?.tableConfiguration;
    const dedupedTableConfiguration = dedupeTableConfiguration(tableConfiguration);

    if (response?.data?.tableConfiguration) {
      response.data.tableConfiguration = dedupedTableConfiguration;
    }

    if (response?.tableConfiguration) {
      response.tableConfiguration = dedupedTableConfiguration;
    }

    return response;
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      autoFetch: true,
      enableApiFiltering: true,
      exportHeaderUnmerge: true,
      baseURL,
      showToolBar: true,
      showPagination: false,
      showTieredMenu: false,
      headers: { token: userProfile?.access_token || '' },
      userInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Shift_Allowance_MIS',
          sheetName: () => 'Allowance MIS',
        },
      },
      handler: {
        handleFilteredApiResponse: handleApiResponse,
      },
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showColumnFilterMenu: true,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'Allowance MIS',
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
        showFilters: true,
        downloadExcelFileName: 'Shift_Allowance_MIS',
        downloadExcelSheetName: 'Allowance MIS',
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: PAGE_SIZE,
      loaderSkeleton: { rows: PAGE_SIZE, bodyCells: 2, columns: 13 },
      paginationConfig: PAGINATION_CONFIG,
      showExpandCollapse: false,
    },
  }), [baseURL, handleApiResponse, userInfo, userProfile?.access_token]);

  return (
    <div className="shift-allowance-mis">
      <TableUtility configuration={configuration} showFilterOption />
    </div>
  );
};

export default ShiftAllowanceMis;
